import {
  Card,
  Chip,
  Stack,
  Button,
  Divider,
  Collapse,
  ListItem,
  Tooltip,
  Typography,
  ListItemText,
  CircularProgress
} from "@material-ui/core";
import { ExpandLess, Visibility } from "@material-ui/icons";
import { useEffect, useMemo, useState } from "react";
import useSettings from "hooks/useSettings";
import { useTranslation } from "react-i18next";
import useContactsLookup from "hooks/contacts/useContactsLookup";
import { getFullName } from "utils/Haim/utils";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import { ListingCardVisits } from "./ListingCardVisits";
import { db } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import { LISTING_PAGE_VIEWS_COLLECTION_NAME } from "../../../@types/sharedSchema";
import type { ContactsLookup } from "hooks/contacts/useContactsLookup";
import type {
  FavoriteListingsOfContact,
  ListingPageViewsDocument,
  RealEstateContact
} from "../../../@types/sharedSchema";

type BuyerMatchedToListingContactsProps = {
  loading: boolean;
  listingId: string;
  /**
   * Read for `id` only. The host builds these from the favourite docs as `{ id }` stand-ins;
   * a whole contact appears only between a pick in its contact picker and the next snapshot.
   */
  selectedContacts: RealEstateContact[];
  favoriteListingsOfContacts: FavoriteListingsOfContact[];
};

/**
 * One line of the list: who the buyer is, and what they did with this listing.
 *
 * Exactly the fields the list reads. The name comes from the contacts lookup, which answers
 * with a projection of the contact — so the row copies the two name fields out of it rather
 * than carrying the answer around as if it were the contact.
 */
type MatchedContactRow = {
  /** The contact's document id (its phone), as the favourite list spells it. */
  id: string;
  callerFirstName?: string;
  callerLastName?: string;
  visited: boolean;
  listingNotes: string;
  pageViews: number;
};

export function BuyerMatchedToListingContacts({
  loading,
  listingId,
  selectedContacts,
  favoriteListingsOfContacts
}: BuyerMatchedToListingContactsProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [viewsMap, setViewsMap] = useState<Record<string, ListingPageViewsDocument>>({});

  const { t: translate } = useTranslation();

  const { themeDirection } = useSettings();

  // The names in the list: one lookup for the buyers on it. Every row below is one of
  // `selectedContacts` (the favourite docs only add what each did with the listing).
  const contactIds = useMemo(() => selectedContacts.map(({ id }) => id), [selectedContacts]);
  const { lookup } = useContactsLookup(contactIds);

  // Fetch view data for all favorite docs
  useEffect(() => {
    if (!favoriteListingsOfContacts.length) return;

    const favoriteIds = favoriteListingsOfContacts.map((fav) => fav.id).filter(Boolean);
    if (!favoriteIds.length) return;

    const fetchViewData = async (): Promise<void> => {
      try {
        const map: Record<string, ListingPageViewsDocument> = {};

        const snapshots = await Promise.all(
          favoriteIds.map((id) =>
            db
              .collection(LISTING_PAGE_VIEWS_COLLECTION_NAME)
              .doc(id)
              .withConverter(GenericConverter<ListingPageViewsDocument>())
              .get()
          )
        );

        snapshots.forEach((snap) => {
          const data = snap.data();
          if (data) map[data.contactId] = data;
        });

        setViewsMap(map);
      } catch (error) {
        console.error("Error fetching view data", error);
      }
    };

    void fetchViewData();
  }, [favoriteListingsOfContacts]);

  const toggleOpen = () => setOpen(!open);

  const isRTL = themeDirection === "rtl";

  const parsedContactsMatchedToListing = favoriteListingsOfContacts
    .filter((contact) => selectedContacts.some((c) => c.id === contact.contactId))
    .map((fav) =>
      getParsedContact({
        contactId: fav.contactId,
        listingId,
        lookup,
        favoriteListingsOfContacts,
        viewsMap
      })
    );

  const shouldAddRestOfMatchedContacts =
    selectedContacts.length > parsedContactsMatchedToListing.length;

  if (shouldAddRestOfMatchedContacts) {
    for (const contact of selectedContacts) {
      const isAlreadyAdded = parsedContactsMatchedToListing.some(
        (parsedContact) => parsedContact.id === contact.id
      );

      if (isAlreadyAdded) continue;

      parsedContactsMatchedToListing.push(
        getParsedContact({
          contactId: contact.id,
          listingId,
          lookup,
          defaultVisited: false,
          favoriteListingsOfContacts,
          viewsMap
        })
      );
    }
  }

  const parsedContactsVisitedListing = parsedContactsMatchedToListing.filter(
    ({ visited }) => visited
  );

  const contactsWhoViewedPage = parsedContactsMatchedToListing.filter(
    ({ pageViews }) => pageViews > 0
  );

  const finalParsedContactsMatchedToListing = parsedContactsMatchedToListing.filter(({ id }) =>
    favoriteListingsOfContacts.find(({ contactId }) => id === contactId)
  );

  return (
    <Stack gap={1}>
      <Button
        sx={{ justifyContent: "flex-start" }}
        color="inherit"
        startIcon={
          loading ? (
            <CircularProgress size={18} />
          ) : (
            <ExpandLess
              sx={{
                transition: "transform 200ms",
                transform: open ? "rotate(0deg)" : isRTL ? "rotate(-90deg)" : "rotate(90deg)"
              }}
            />
          )
        }
        disabled={loading}
        onClick={toggleOpen}
      >
        {translate("listingMatchToContactsPage.potentialBuyersFoundAndContactVisited", {
          potentialBuyersCount: selectedContacts.length,
          visitedContactsCount: parsedContactsVisitedListing.length
        })}
        {contactsWhoViewedPage.length > 0 && (
          <Chip
            size="small"
            icon={<Visibility sx={{ fontSize: 14 }} />}
            label={translate("listingMatchToContactsPage.contactsViewedPage", {
              count: contactsWhoViewedPage.length
            })}
            color="success"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Card sx={{ p: 2 }} variant="outlined">
          <Stack gap={1}>
            {finalParsedContactsMatchedToListing.length ? (
              <Stack
                sx={{
                  py: 1,
                  maxHeight: 300,
                  overflowY: "auto"
                }}
                divider={<Divider />}
                gap={1}
              >
                {finalParsedContactsMatchedToListing.map(
                  ({ id, callerFirstName, callerLastName, listingNotes, pageViews }) => {
                    const callerPhoneNumber = parsePhoneNumber(id)?.formatNational() ?? id;

                    const callerFullName = getFullName(callerFirstName, callerLastName);

                    const primaryText = callerFullName || callerPhoneNumber;
                    const secondaryText = callerFullName ? callerPhoneNumber : null;

                    const favoriteListingOfContact = favoriteListingsOfContacts.find(
                      (fav) => fav.contactId === id
                    );

                    return (
                      <ListItem
                        secondaryAction={
                          <Stack direction="row" gap={1} alignItems="center">
                            {pageViews > 0 && (
                              <Tooltip
                                title={translate(
                                  "listingMatchToContactsPage.contactViewedPageCount",
                                  { count: pageViews }
                                )}
                              >
                                <Chip
                                  size="small"
                                  icon={<Visibility sx={{ fontSize: 14 }} />}
                                  label={pageViews}
                                  color="success"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                            <ListingCardVisits
                              disabled={loading}
                              listingId={listingId}
                              favoriteListingsOfContact={favoriteListingOfContact}
                            />
                          </Stack>
                        }
                        key={id}
                      >
                        <ListItemText
                          primary={
                            <Stack direction="row" gap={1}>
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                {...(loading && { color: "text.disabled" })}
                              >
                                {primaryText}
                              </Typography>
                              {secondaryText && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  {...(loading && { color: "text.disabled" })}
                                >
                                  ({secondaryText})
                                </Typography>
                              )}
                            </Stack>
                          }
                          secondary={listingNotes}
                        />
                      </ListItem>
                    );
                  }
                )}
              </Stack>
            ) : (
              <Typography sx={{ p: 2 }} variant="body2" color="text.secondary" textAlign="center">
                {translate("listingMatchToContactsPage.error.noContactsVisitedThisListing")}
              </Typography>
            )}
          </Stack>
        </Card>
      </Collapse>
    </Stack>
  );
}

type GetParsedContactOptions = {
  contactId: string;
  listingId: string;
  lookup: ContactsLookup["lookup"];
  defaultVisited?: boolean;
  favoriteListingsOfContacts: FavoriteListingsOfContact[];
  viewsMap: Record<string, ListingPageViewsDocument>;
};

function getParsedContact({
  contactId,
  listingId,
  lookup,
  defaultVisited,
  favoriteListingsOfContacts,
  viewsMap
}: GetParsedContactOptions): MatchedContactRow {
  // Not answered yet and "not in the book" both leave the row nameless: it shows the phone.
  const contact = lookup(contactId);

  const favorite = favoriteListingsOfContacts.find((fav) => fav.contactId === contactId);

  const visited = defaultVisited ?? favorite?.listingIdsUserVisited?.includes(listingId) ?? false;

  const listingNotes = favorite?.additionalDetails?.[listingId]?.listingNotes ?? "";

  const contactViewData = viewsMap[contactId];
  const pageViews = contactViewData?.listingDetailViewCounts?.[listingId] ?? 0;

  return {
    // The id as the favourite list spells it, never the looked-up one: the rows are matched
    // back to `favoriteListingsOfContacts` and `selectedContacts` by this exact string.
    id: contactId,
    callerFirstName: contact?.callerFirstName,
    callerLastName: contact?.callerLastName,
    visited,
    listingNotes,
    pageViews
  };
}
